/**
 * app-config.ts
 * Reads runtime configuration injected by public/settings.js via window.__APP_CONFIG__.
 * If a property is missing or the file was not loaded, a safe default is used and
 * a warning is printed to the console so the issue is easy to diagnose.
 */

interface ApiConfig {
    baseUrl: string;
}

interface AppConfig {
    api: ApiConfig;
    boldr: ApiConfig;
}

// Extend the global Window interface so TypeScript accepts window.__APP_CONFIG__
declare global {
    interface Window {
        __APP_CONFIG__?: DeepPartial<AppConfig>;
    }
}

// Utility type: makes all nested properties optional
type DeepPartial<T> = { [K in keyof T]?: DeepPartial<T[K]> };

const DEFAULTS: AppConfig = {
    api: {
        baseUrl: "https://warehousestockmanager.dev.api.prosapp.nl"
    },
    boldr: {
        baseUrl: "https://ocr.boldr.online"
    }
};

function warnAndFallback(key: string, fallback: string): string {
    console.warn(
        "[AppConfig] '" + key + "' not found in settings.js — falling back to default: " + fallback
    );
    return fallback;
}

function buildConfig(): AppConfig {
    const external = window.__APP_CONFIG__ || {};

    const apiBaseUrl = (external.api && external.api.baseUrl)
        ? external.api.baseUrl
        : warnAndFallback("api.baseUrl", DEFAULTS.api.baseUrl);

    const boldrBaseUrl = (external.boldr && external.boldr.baseUrl)
        ? external.boldr.baseUrl
        : warnAndFallback("boldr.baseUrl", DEFAULTS.boldr.baseUrl);

    return {
        api: { baseUrl: apiBaseUrl },
        boldr: { baseUrl: boldrBaseUrl }
    };
}

/** Resolved application configuration, available synchronously at module load time. */
export const appConfig: AppConfig = buildConfig();
